import type { DesignValidationResult } from "./DesignBuildSummary";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ArrowLeft, ArrowRight } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: DesignValidationResult;
  onContinue: () => void;
  onGoBack: () => void;
  onProceedToPricing?: () => void;
}

export function DesignValidationDialog({ open, onOpenChange, result, onContinue, onGoBack, onProceedToPricing }: Props) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-9 w-9 rounded-lg bg-warning/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-warning" />
            </div>
            <AlertDialogTitle className="text-base">Missing Design Selections</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed">
            You have not selected the following items for this build:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          {result.recommendedMissing.map(item => (
            <div key={item} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-warning/5 border border-warning/20">
              <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
              <span className="text-sm font-medium">No {item.toLowerCase()} selected</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">Is this correct? You can go back to add selections or continue without them.</p>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onGoBack} className="gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" />
            Go Back and Review
          </AlertDialogCancel>
          <div className="flex gap-2">
            <AlertDialogAction onClick={onContinue} className="gap-1.5">
              Continue Anyway
            </AlertDialogAction>
            {onProceedToPricing && (
              <AlertDialogAction onClick={onProceedToPricing} className="gap-1.5 bg-primary">
                <ArrowRight className="h-3.5 w-3.5" />
                Continue to Pricing
              </AlertDialogAction>
            )}
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
